import axios from "axios";
import { defaultAdSpendAllocations, defaultMonthlySummary, monthsLowerCase } from "../consts/forecast";

export const redirectToTheQuickBooksAuth = async () => {
    try {
      const { data } = await axios.get('api/v1/auth-quickbooks');
      window.location.href = data.url;
    } catch (error) {
      console.error(error)
    }
}

export const formatPercent = (value, withoutTransform) => {
  if (!value) return "-"
  return `${value.toFixed(2)}${withoutTransform ? "" : "%"}`
}

export const formatAmount = (value, withoutTransform = false, fractionDigits = 2) => {
   if (!value) return "-";
   return `${withoutTransform ? "" : "$"}${Number(value).toLocaleString("en-US", { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;
};

export const formatBudgetAmount = (value) => {
  const normalizedInput = String(value ?? "").replace(/[$,\s]/g, "");
  const normalizedValue = Number(normalizedInput);
  const formattedValue = normalizedInput !== "" && Number.isFinite(normalizedValue) && normalizedValue === 0
    ? "$0.00"
    : formatAmount(value);
  return normalizedValue < 0 ? formattedValue.replace(/^\$-/, "-$") : formattedValue;
};

export const formatBudgetInputAmount = (value) => {
  const normalizedInput = String(value ?? "").replace(/[$,\s]/g, "");
  if (normalizedInput === "") return "";

  const normalizedValue = Number(normalizedInput);
  if (!Number.isFinite(normalizedValue)) return "";

  return normalizedValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const budgetInputEditText = (value) => {
  const normalizedInput = String(value ?? "").replace(/[$,\s]/g, "");
  if (normalizedInput === "") return "";

  const normalizedValue = Number(normalizedInput);
  if (!Number.isFinite(normalizedValue)) return "";

  return normalizedValue.toFixed(2);
};

export const fillAllMonthsWithPrevious = (
  currentData,
  previousData
) => {
  const filled = {};

  monthsLowerCase.forEach((month) => {
    if (currentData[month] !== undefined) {
      filled[month] = currentData[month];
    } else if (previousData[month] !== undefined) {
      filled[month] = previousData[month];
    } else {
      filled[month] = 0;
    }
  });

  return filled;
}

export const fillAllMonths = (value) => Object.fromEntries(monthsLowerCase.map(month => [month, value]));

export const marginAdSpendCalculation = ({
    adSpendMer,
    productAdSpend,
    beroasAdSpend,
}) => {
    const calculateContributionMargin = () => {
        const mer = Number.parseFloat(adSpendMer);
        const adSpend = Number.parseFloat(productAdSpend);
        const beroas = Number.parseFloat(beroasAdSpend);
        if (beroas === 0) return 0
        return (mer * adSpend) / beroas - adSpend;
    }
    return calculateContributionMargin();
}

export const quantityAdSpendCalculation = ({
    adSpendMer,
    productAdSpend,
    aovAdSpend
}) => {
    const currentMER = parseFloat(adSpendMer);
    const currentProduct = parseFloat(productAdSpend);
    const currentAOV = parseFloat(aovAdSpend);
    if (
        !isNaN(currentMER) &&
        !isNaN(currentProduct) &&
        !isNaN(currentAOV) &&
        currentAOV !== 0
    ) {
        return (currentMER * currentProduct) / currentAOV;
    } else {
        return 0;
    }
}

export const revenueAdSpendCalculation = ({
    adSpendMer,
    productAdSpend
}) => {
    const currentMER = parseFloat(adSpendMer);
    const currentProduct = parseFloat(productAdSpend);
    if (!isNaN(currentMER) && !isNaN(currentProduct)) {
        return currentMER * currentProduct
    } else {
        return 0;
    }
}

export const annualRevenueCalculation = ({
    merYear,
    annualAdSpendYear
}) => {
    const currentMER = parseFloat(merYear);
    const currentProduct = parseFloat(annualAdSpendYear);
    if (!isNaN(currentMER) && !isNaN(currentProduct)) {
        return currentMER * currentProduct;
    } else {
        return 0;
    }
}

export const annualUnitsCalculation = ({
    merYear,
    annualAdSpendYear,
    aovYear
}) => {
    const currentMER = parseFloat(merYear);
    const currentProduct = parseFloat(annualAdSpendYear);
    const currentAov = parseFloat(aovYear);
    if (
        !isNaN(currentMER) &&
        !isNaN(currentProduct) &&
        !isNaN(currentAov) &&
        currentAov !== 0
    ) {
        return (currentMER * currentProduct) / currentAov;
    } else {
        return 0;
    }
}

export const recalculateMonthlySummaryByYear = (monthlySummaryByYear, monthlyAllocationData) => {
  const updatedSummary = {};

  for (const [categoryKey, channels] of Object.entries(monthlySummaryByYear)) {
    updatedSummary[categoryKey] = {};

    for (const [channelKey, monthData] of Object.entries(channels)) {
      updatedSummary[categoryKey][channelKey] = {};

      for (const [month, value] of Object.entries(monthData)) {
        const percent = monthlyAllocationData[month] || 0;
        updatedSummary[categoryKey][channelKey][month] = Number(((value || 0) * percent / 100).toFixed(2));
      }
    }
  }

  return updatedSummary;
};


const deepMerge = (target, source) => {
    if (typeof target !== 'object' || typeof source !== 'object' || target === null || source === null) {
        return source;
    }

    const output = { ...target };

    for (const key of Object.keys(source)) {
        if (key in target) {
            output[key] = deepMerge(target[key], source[key]);
        } else {
            output[key] = source[key];
        }
    }

    return output;
};

export const getMergedForecastData = (forecastData, editedForecastData, selectedProduct, isYear = false) => {
    const path = isYear ? 'year' : 'month';
    const baseData = isYear
        ? forecastData?.[path]?.[selectedProduct] || {}
        : forecastData?.[path]?.[selectedProduct]?.months || {};

    const editedData = isYear
        ? editedForecastData?.[path]?.[selectedProduct] || {}
        : editedForecastData?.[path]?.[selectedProduct]?.months || {};

    const mergedData = { ...baseData };

    for (const [key, editedValue] of Object.entries(editedData)) {
        if (mergedData[key]) {
            mergedData[key] = deepMerge(mergedData[key], editedValue);
        } else {
            mergedData[key] = editedValue;
        }
    }

    return mergedData;
};

export const getEditedData = ({
    forecastData,
    editedForecastData,
    selectedProduct
}) => {
        const existingForecastData = forecastData?.month?.[selectedProduct]?.months;
        const existingEditedData = editedForecastData?.month?.[selectedProduct]?.months;

        const newData = {};

        monthsLowerCase.forEach((month) => {
            newData[month] = {
                allocate_ad_spend: false,
                forecast_method: "ad-spend",
                ad_spend: {
                    mer: 0,
                    product_ad_spend: 0,
                    aov: 0,
                    revenue: 0,
                    quantity: 0,
                    beroas: 0,
                    contribution_margin: 0
                },
                manual_revenue: {
                    revenue_target: 0,
                    aov: 0
                },
                channel_split: {
                    shopify: 0,
                    amazon: 0,
                    wholesale: 0
                },
                ad_spend_allocations: defaultAdSpendAllocations,
                monthly_summary: {
                    "ad_spend": defaultMonthlySummary,
                    "revenue_target": defaultMonthlySummary
                }
            }
        })
        if (existingForecastData) {
            Object.keys(existingForecastData).forEach((month) => {
                if (newData[month]) {
                    newData[month] = {
                        ...newData[month],
                        ...existingForecastData[month]
                    };
                }
            });
        }
        if (existingEditedData) {
            Object.keys(existingEditedData).forEach((month) => {
                if (newData[month]) {
                    newData[month] = {
                        ...newData[month],
                        ...existingEditedData[month]
                    };
                }
            });
        }
        return newData;
}